import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  ArCustomerCommsSummary,
  CustomerCollectionStatus,
} from '@erp/rpc';

/**
 * Fetches comms summary (collection status + recent activity) for a set of
 * customers visible in the AR reports view. Skipped while the list is empty.
 */
export function useArCustomerCommsSummary(customerIds: string[]) {
  const queryOptions =
    orpc.accountsReceivable.arCustomerComms.getCommsSummary.queryOptions({
      input: { customerIds },
      enabled: customerIds.length > 0,
    });

  const query = useQuery(queryOptions);

  const byCustomerId = new Map<string, ArCustomerCommsSummary>(
    (query.data?.summaries ?? []).map((s) => [s.customerId, s]),
  );

  return {
    ...query,
    byCustomerId,
    queryKey: queryOptions.queryKey,
  };
}

export function useSetCollectionStatus(
  invalidateKeys: readonly QueryKey[] = [],
) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCustomerComms.setCollectionStatus.mutationOptions(
      {
        onSuccess: () => {
          for (const key of invalidateKeys) {
            void queryClient.invalidateQueries({ queryKey: key });
          }
        },
      },
    ),
  );
}

/**
 * Toggles a customer's automated-communication opt-outs (payment reminders /
 * invoice emails). Invalidates the customer profile so the toggle state and any
 * dependent views reflect the change.
 */
export function useSetCommsExclusions() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCustomerComms.setCommsExclusions.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.arCustomerProfile.get.key(),
        });
      },
    }),
  );
}

export function useSetRemittanceType() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCustomerComms.setRemittanceType.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.arCustomerProfile.get.key(),
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.arCashMetrics.get.key(),
        });
      },
    }),
  );
}

export function useAddCustomerNote(invalidateKeys: readonly QueryKey[] = []) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCustomerComms.addCustomerNote.mutationOptions({
      onSuccess: () => {
        for (const key of invalidateKeys) {
          void queryClient.invalidateQueries({ queryKey: key });
        }
      },
    }),
  );
}

export function useEditCustomerNote(invalidateKeys: readonly QueryKey[] = []) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCustomerComms.editCustomerNote.mutationOptions({
      onSuccess: () => {
        for (const key of invalidateKeys) {
          void queryClient.invalidateQueries({ queryKey: key });
        }
      },
    }),
  );
}

export function useDeleteCustomerNote(
  invalidateKeys: readonly QueryKey[] = [],
) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arCustomerComms.deleteCustomerNote.mutationOptions({
      onSuccess: () => {
        for (const key of invalidateKeys) {
          void queryClient.invalidateQueries({ queryKey: key });
        }
      },
    }),
  );
}

export type { ArCustomerCommsSummary, CustomerCollectionStatus };
